// Formula custom fields — grammar, field vocabulary and result-type inference.
//
// The expression string is a CONTRACT with the backend evaluator (coffee
// `src/python/crm/formula/grammar.py`). Field references are bare canonical
// keys, never display labels:
//
//   - custom field  -> its `crmName` (e.g. `arr_857_coffee`), the same key the
//     value is materialized under at `properties.<crmName>`.
//   - standard field -> its backend property name (e.g. `close_date`). Those
//     come from the same `getCrmIngestionFields` query as custom fields (the
//     rows flagged `isStandardField`), so anything we offer here is guaranteed
//     to exist as a `crm_ingestion_field` row — which is exactly what the
//     backend validates references against.
//
// Formula fields created in Lovable preview before this contract existed stored
// *labels* in the expression ("AVERAGE(Amount)"). `resolveFormulaField` accepts
// those leniently and converts them back to keys; anything it still cannot
// resolve is surfaced as a broken token rather than crashing the builder.

/**
 * A field that can be referenced from a formula (standard or custom).
 *
 * `label` is NOT translated, and cannot be. Three readers match on it rather
 * than merely showing it: `resolveFormulaField` accepts a label as an alias for
 * a key so pre-contract preview formulas ("AVERAGE(Amount)") still open;
 * `interpret-formula.ts` scores labels against what the user typed to turn
 * natural language into an expression; and for every custom and standard field
 * the label arrives FROM the workspace's own registry, which is user data
 * (GLOSSARY.md rule 3). Translating the handful this module declares itself
 * would make the vocabulary half-translated and break the two matchers for the
 * fields it touched. Relation and kind labels are the parts with a display path
 * — see `formulaRelationLabel` / `formulaKindHelp`.
 */
export interface FormulaFieldOption {
  key: string;
  label: string;
  type: string;
  /**
   * FORMULA fields only: what the referenced formula evaluates to
   * (`formula_result_type` — "number" | "string" | "date"). A formula field's
   * `type` is always `"formula"`, which says nothing about whether SUM/AVERAGE
   * can use it: `IF(stage = "Closed Won", "yes", "no")` is a formula that
   * yields text. Empty/absent means unknown, and unknown is treated
   * permissively (see `pickFormulaFields`).
   */
  resultType?: string;
  /**
   * Resolvable but not offered in the picker. For a key the backend accepts as
   * an alias of another one it already offers (`LOOKUP(owner, name)` vs
   * `full_name`): listing both would show the user two identical choices, but
   * omitting the alias entirely would render a stored formula that uses it as a
   * broken token and block saving. See `OWNER_LOOKUP_FIELDS`.
   */
  hidden?: boolean;
}

export type FormulaKind = "average" | "count" | "datedif" | "if" | "lookup" | "sum";

/** What the evaluated value looks like — drives the OpenSearch sort subfield. */
export type FormulaResultType = "number" | "string" | "date";

export type FormulaSegment =
  | { lit: string }
  | {
      slot: string;
      kind: "field" | "value";
      /** English default. Render `formulaPlaceholder(segment, t)` instead. */
      placeholder: string;
      /** Catalog key for the translated placeholder; absent ⇒ render as authored. */
      placeholderKey?: string;
      numeric?: boolean;
      dateOnly?: boolean;
    };

/** Kinds that take a variable number of same-record fields: SUM(a, b, c). */
export const VARIADIC_KINDS: FormulaKind[] = ["sum", "average", "count"];

export function isVariadicKind(kind: FormulaKind): boolean {
  return VARIADIC_KINDS.includes(kind);
}

/**
 * `label` is the grammar's own function name — AVERAGE, DATEDIF, LOOKUP — and it
 * is what the expression serializes to (`serializeFormula` upper-cases `kind`
 * for the variadic forms and writes `DATEDIF(`/`LOOKUP(` literally). It is an
 * identifier and stays English, per GLOSSARY.md rule 2. `help` is prose about
 * it and is translated, via `helpKey`.
 */
export const FORMULA_KINDS: {
  kind: FormulaKind;
  label: string;
  help: string;
  helpKey: string;
  segments: FormulaSegment[];
}[] = [
  // Alphabetical.
  {
    kind: "average", label: "AVERAGE",
    help: "Averages the selected numeric fields on this record. Empty fields are left out of the average.",
    helpKey: "dataModelFields.formula.help.average",
    segments: [],
  },
  {
    kind: "count", label: "COUNT",
    help: "Counts how many of the selected fields have a value on this record.",
    helpKey: "dataModelFields.formula.help.count",
    segments: [],
  },
  {
    kind: "datedif", label: "DATEDIF",
    help: 'Number of whole days between two date fields (Excel: DATEDIF(start, end, "d")).',
    helpKey: "dataModelFields.formula.help.datedif",
    segments: [
      { lit: "DATEDIF(" },
      {
        slot: "a", kind: "field", placeholder: "start date",
        placeholderKey: "dataModelFields.formula.placeholders.startDate", dateOnly: true,
      },
      { lit: ", " },
      {
        slot: "b", kind: "field", placeholder: "end date",
        placeholderKey: "dataModelFields.formula.placeholders.endDate", dateOnly: true,
      },
      { lit: ', "d")' },
    ],
  },
  {
    kind: "if", label: "IF/THEN",
    // Rendered by a dedicated condition builder (multiple AND/OR rows), so no
    // token segments here.
    help: "Returns one value when the condition(s) are true and another when false. Numeric results stay unquoted so they can feed SUM/AVERAGE.",
    helpKey: "dataModelFields.formula.help.if",
    segments: [],
  },
  {
    kind: "lookup", label: "LOOKUP",
    // Rendered by a dedicated relation picker + field token, so no segments.
    help: "Pulls a field from a related record (for example the company on a deal).",
    helpKey: "dataModelFields.formula.help.lookup",
    segments: [],
  },
  {
    kind: "sum", label: "SUM",
    help: "Adds up the selected numeric fields on this record. Empty fields count as zero.",
    helpKey: "dataModelFields.formula.help.sum",
    segments: [],
  },
];

/** The `t` from `useTranslation()`, narrowed to what this module needs. */
export type FormulaTranslator = (key: string, options?: Record<string, unknown>) => string;

/** The explanation under the formula-kind picker. */
export function formulaKindHelp(kind: FormulaKind, t: FormulaTranslator): string {
  const entry = FORMULA_KINDS.find(k => k.kind === kind);
  return entry ? t(entry.helpKey) : "";
}

/** The placeholder inside one token slot ("start date"). */
export function formulaPlaceholder(
  segment: { placeholder: string; placeholderKey?: string },
  t: FormulaTranslator,
): string {
  return segment.placeholderKey ? t(segment.placeholderKey) : segment.placeholder;
}

export const NUMERIC_TYPES = ["number", "currency", "formula", "percent", "rating", "score"];
export const DATE_TYPES = ["date", "datetime"];

/**
 * The clock, spelled exactly as the backend grammar serializes it.
 *
 * Not a field and not a `FormulaKind` — it occupies a date SLOT, so "days since
 * X" is `DATEDIF(x, TODAY(), "d")`. Modelling it as a pseudo-field lets it flow
 * through the token picker, the chip label and the resolver without a parallel
 * code path.
 *
 * Its value refreshes once a day from a backend schedule, not on save — a record
 * nobody touches still advances.
 */
export const TODAY_TOKEN = "TODAY()";

export const TODAY_OPTION: FormulaFieldOption = {
  key: TODAY_TOKEN,
  label: "Today",
  type: "date",
};

/**
 * The deal field whose stored value is an opaque external id rather than the name shown
 * everywhere else, so an IF condition on it needs a picker, not a text box.
 */
export const STAGE_FIELD = "stage";

export function isTodayToken(token: string): boolean {
  return (token || "").trim().toUpperCase().replace(/\s+/g, "") === TODAY_TOKEN;
}

/** The date fields on offer, plus Today. Only for slots that accept a date. */
export function withTodayOption(fields: FormulaFieldOption[]): FormulaFieldOption[] {
  return [TODAY_OPTION, ...fields];
}

export const FORMULA_OPERATORS = ["=", "<>", ">", ">=", "<", "<="] as const;

export interface FormulaCondition { field: string; op: string; value: string }
export interface IfFormula {
  conds: FormulaCondition[];
  join: "AND" | "OR";
  then: string;
  else: string;
}

export const EMPTY_IF: IfFormula = {
  conds: [{ field: "", op: "=", value: "" }],
  join: "AND",
  then: "",
  else: "",
};

// --- Related-record vocabulary (LOOKUP) -------------------------------------

export type FormulaObjectKey = "people" | "companies" | "deals" | "notes" | "tasks";

export interface FormulaRelationDef {
  /** Relation name as it appears in the expression: LOOKUP(<relation>, field). */
  relation: string;
  /** English default. Render `formulaRelationLabel(relation, t)` instead. */
  label: string;
  /** Catalog key for the translated label; absent ⇒ render as authored. */
  labelKey?: string;
  /**
   * Which object's fields the second argument is picked from. Absent for a
   * relation that does not point at a CRM object — see `fields`.
   */
  targetObject?: FormulaObjectKey;
  /**
   * A fixed field list, for relations whose far side is not a CRM object and so
   * has no workspace field catalogue to draw from (`owner`). When set, this is
   * used verbatim and `targetObject` is ignored.
   */
  fields?: FormulaFieldOption[];
}

/**
 * The record's owner: the workspace user the record belongs to, not another CRM
 * record. The backend resolves it down its own path (`formula/service.py`
 * `_fetch_owners`) rather than through the stored association ids.
 */
export const OWNER_RELATION = "owner";

/**
 * The ONLY owner attributes a formula may read. This mirrors
 * `planner.OWNER_FIELDS` in coffee exactly — the backend validates
 * `LOOKUP(owner, x)` against that allow-list and rejects anything else, so
 * offering a field here that isn't in it would produce a formula that fails to
 * save. It is a deliberately small PII surface: a formula result is
 * materialized into the record's properties and projected into OpenSearch.
 *
 * `name` is the backend's alias of `full_name`. It stays resolvable but hidden,
 * because the AI generator is offered both spellings (`api/formula_field.py`
 * `_relation_entries` sends all of `OWNER_FIELDS`) and can emit either — a
 * stored `LOOKUP(owner, name)` must open in the builder rather than render as a
 * broken token. Only one of the two is offered in the picker.
 */
export const OWNER_LOOKUP_FIELDS: FormulaFieldOption[] = [
  { key: "full_name", label: "Full Name", type: "text" },
  { key: "first_name", label: "First Name", type: "text" },
  { key: "last_name", label: "Last Name", type: "text" },
  { key: "email", label: "Email", type: "text" },
  { key: "name", label: "Name", type: "text", hidden: true },
];

/**
 * The owner relation, labelled the way THIS object's UI names that person.
 *
 * Deals call it "Owner". Companies and People have no Owner column at all —
 * the same `properties.owner_id` surfaces as **Created By** there (see
 * `grid/grid-column-config.ts` and `data-model/standard-fields.ts`), so
 * labelling it "Owner" would name a field the user cannot find anywhere else
 * on the object. The relation token in the expression is always `owner`
 * regardless — that spelling is the backend contract.
 */
const ownerRelation = (label: string, labelKey: string): FormulaRelationDef => ({
  relation: OWNER_RELATION,
  label,
  labelKey,
  fields: OWNER_LOOKUP_FIELDS,
});

/**
 * Relations a formula can traverse, per source object. Deliberately one hop —
 * the backend resolves the related record from the entity's stored association
 * ids, and a formula on the related record is read at its materialized value.
 *
 * Must stay a subset of what the backend accepts: `planner.RELATION_ID_FIELDS`
 * plus `owner` on every formula-capable object type (ACCOUNT / CONTACT / DEAL).
 * Notes and tasks support no formulas at all, hence no relations.
 */
export const FORMULA_RELATIONS: Record<FormulaObjectKey, FormulaRelationDef[]> = {
  deals: [
    {
      relation: "company", label: "Company", targetObject: "companies",
      labelKey: "dataModelFields.formula.relations.deals.company",
    },
    {
      relation: "contact", label: "Primary Contact", targetObject: "people",
      labelKey: "dataModelFields.formula.relations.deals.contact",
    },
    ownerRelation("Owner", "dataModelFields.formula.relations.deals.owner"),
  ],
  people: [
    {
      relation: "company", label: "Company", targetObject: "companies",
      labelKey: "dataModelFields.formula.relations.people.company",
    },
    ownerRelation("Created By", "dataModelFields.formula.relations.people.owner"),
  ],
  // An account stores no related-record ids, so `owner` is its only relation —
  // which is also what makes LOOKUP available on Companies at all.
  companies: [ownerRelation("Created By", "dataModelFields.formula.relations.companies.owner")],
  notes: [],
  tasks: [],
};

/** The display label for a LOOKUP relation in the picker. */
export function formulaRelationLabel(
  relation: Pick<FormulaRelationDef, "label" | "labelKey">,
  t: FormulaTranslator,
): string {
  return relation.labelKey ? t(relation.labelKey) : relation.label;
}

/** A relation plus the fields selectable on the far side of it. */
export interface FormulaLookupRelation extends FormulaRelationDef {
  fields: FormulaFieldOption[];
}

/**
 * Resolve this object's relations to concrete field lists for the builder.
 *
 * A relation with a fixed `fields` list (owner) uses it as-is; one that points
 * at a CRM object draws that object's formula-eligible fields from the caller's
 * catalogue.
 */
export function lookupRelationsFor(
  object: FormulaObjectKey,
  fieldsFor: (target: FormulaObjectKey) => FormulaFieldOption[],
): FormulaLookupRelation[] {
  return FORMULA_RELATIONS[object].map(r => ({
    ...r,
    fields: r.fields ?? (r.targetObject ? fieldsFor(r.targetObject) : []),
  }));
}

// --- Field vocabulary --------------------------------------------------------

/**
 * Build the selectable field list for a formula: standard fields first, then
 * custom, deduped by key. `crmName` is the key that goes into the expression.
 */
export function formulaFieldOptionsFor(
  fields: Array<{ crmName: string; label: string; type: string; formulaResultType?: string }>,
): FormulaFieldOption[] {
  const seen = new Set<string>();
  const out: FormulaFieldOption[] = [];
  for (const f of fields) {
    const key = (f.crmName || "").trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push({
      key,
      label: f.label || key,
      type: f.type || "text",
      ...(f.formulaResultType?.trim() ? { resultType: f.formulaResultType.trim() } : {}),
    });
  }
  return out;
}

export type FormulaFieldResolution =
  | { status: "ok"; option: FormulaFieldOption }
  | { status: "missing"; token: string };

/**
 * Resolve a token from a stored expression to a selectable field.
 *
 * Exact key match wins. A label match is accepted so formulas authored before
 * expressions stored keys ("AVERAGE(Amount)") still open in the builder — the
 * caller rewrites the token to `option.key` on load. Everything else is
 * reported missing so the builder can render a broken token and block saving
 * rather than persisting a dangling reference.
 */
export function resolveFormulaField(
  token: string,
  fields: FormulaFieldOption[],
): FormulaFieldResolution {
  const raw = (token || "").trim();
  if (!raw) return { status: "missing", token: raw };
  // TODAY() resolves without being in the pool. It is not a field on any object, so
  // every caller that passes a field list would otherwise report it broken — striking
  // the chip through and blocking save on a formula the backend accepts.
  if (isTodayToken(raw)) return { status: "ok", option: TODAY_OPTION };
  const byKey = fields.find(f => f.key === raw);
  if (byKey) return { status: "ok", option: byKey };
  const lowered = raw.toLowerCase();
  const byLabel = fields.find(f => f.label.trim().toLowerCase() === lowered);
  if (byLabel) return { status: "ok", option: byLabel };
  return { status: "missing", token: raw };
}

/** Display text for a token: the field's label when known, else the raw token. */
export function formulaTokenLabel(token: string, fields: FormulaFieldOption[]): string {
  const res = resolveFormulaField(token, fields);
  return res.status === "ok" ? res.option.label : token;
}

/**
 * Is this field usable in a slot restricted to `types`?
 *
 * A FORMULA field passes the numeric/date gate on its RESULT type, not on the
 * literal type `"formula"`. `NUMERIC_TYPES` lists `"formula"` because a formula
 * *can* be numeric — but `IF(stage = "Closed Won", "won", "lost")` is a formula
 * that yields text, and offering it as an AVERAGE source produced a column that
 * silently evaluated to nothing. A formula whose result type is unknown (no
 * `formula_result_type` stamped — possible for rows written before the backend
 * started filling it in) stays eligible, so this tightens the picker without
 * making existing formulas vanish from it.
 */
export function isFormulaFieldEligible(f: FormulaFieldOption, types?: string[]): boolean {
  if (!types) return true;
  if (!types.includes(f.type)) return false;
  if (f.type !== "formula") return true;
  const result = (f.resultType || "").trim().toLowerCase();
  if (!result) return true;
  if (types.includes("number")) return result === "number";
  if (types.includes("date")) return result === "date";
  return true;
}

/** Only show fields whose type is valid for the slot (numeric/date). */
export function pickFormulaFields(all: FormulaFieldOption[], types?: string[]): FormulaFieldOption[] {
  if (!types) return all;
  return all.filter(f => isFormulaFieldEligible(f, types));
}

// --- Serialization -----------------------------------------------------------

/** Numbers are emitted unquoted so IF can return 1/0 for roll-ups. */
function isNumericLiteral(v: string) {
  return v.trim() !== "" && !Number.isNaN(Number(v.trim()));
}
function literal(v: string) {
  const t = v.trim();
  if (isNumericLiteral(t)) return t;
  if (/^(TRUE|FALSE)$/i.test(t)) return t.toUpperCase();
  return `"${t.replace(/"/g, '\\"')}"`;
}
function unliteral(v: string) {
  const t = v.trim();
  const m = t.match(/^"(.*)"$/s);
  return m ? m[1].replace(/\\"/g, '"') : t;
}

/** Split on commas that are not inside parens or quotes. */
export function splitTopLevel(src: string): string[] {
  const out: string[] = [];
  let depth = 0, quoted = false, cur = "";
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (quoted) {
      cur += c;
      if (c === '"' && src[i - 1] !== "\\") quoted = false;
      continue;
    }
    if (c === '"') { quoted = true; cur += c; continue; }
    if (c === "(") depth++;
    if (c === ")") depth--;
    if (c === "," && depth === 0) { out.push(cur); cur = ""; continue; }
    cur += c;
  }
  if (cur.trim()) out.push(cur);
  return out.map(s => s.trim());
}

export function serializeIf(f: IfFormula): string {
  const conds = f.conds.filter(c => c.field.trim() && c.op.trim() && c.value.trim());
  if (!conds.length || conds.length !== f.conds.length) return "";
  if (!f.then.trim() || !f.else.trim()) return "";
  const parts = conds.map(c => `${c.field} ${c.op} ${literal(c.value)}`);
  const cond = parts.length === 1 ? parts[0] : `${f.join}(${parts.join(", ")})`;
  return `IF(${cond}, ${literal(f.then)}, ${literal(f.else)})`;
}

function parseCondition(src: string): FormulaCondition | null {
  const m = src.match(/^(.+?)\s*(>=|<=|<>|!=|=|>|<)\s*(.+)$/);
  if (!m) return null;
  return { field: m[1].trim(), op: m[2] === "!=" ? "<>" : m[2], value: unliteral(m[3]) };
}

export function parseIf(src: string): IfFormula | null {
  const m = (src || "").trim().match(/^IF\(([\s\S]*)\)$/i);
  if (!m) return null;
  const parts = splitTopLevel(m[1]);
  if (parts.length < 3) return null;
  const elseVal = parts[parts.length - 1];
  const thenVal = parts[parts.length - 2];
  const condSrc = parts.slice(0, parts.length - 2).join(", ");
  let join: "AND" | "OR" = "AND";
  let condParts = [condSrc];
  const grouped = condSrc.match(/^(AND|OR)\(([\s\S]*)\)$/i);
  if (grouped) {
    join = grouped[1].toUpperCase() as "AND" | "OR";
    condParts = splitTopLevel(grouped[2]);
  }
  const conds = condParts.map(parseCondition).filter(Boolean) as FormulaCondition[];
  if (!conds.length) return null;
  return { conds, join, then: unliteral(thenVal), else: unliteral(elseVal) };
}

export type FormulaSlots = Record<string, string>;

/** Everything the builder needs to render (and re-serialize) one formula. */
export interface FormulaState {
  kind: FormulaKind;
  slots: FormulaSlots;
  /** Field keys for the variadic kinds (SUM/AVERAGE/COUNT). */
  args: string[];
  ifState?: IfFormula;
}

export function formulaSegments(kind: FormulaKind): FormulaSegment[] {
  return FORMULA_KINDS.find(k => k.kind === kind)?.segments ?? [];
}

const VARIADIC_FN: Record<string, FormulaKind> = { SUM: "sum", AVERAGE: "average", COUNT: "count" };

export function serializeFormula(
  kind: FormulaKind,
  slots: FormulaSlots,
  ifState?: IfFormula,
  args: string[] = [],
): string {
  if (kind === "if") return ifState ? serializeIf(ifState) : "";
  if (isVariadicKind(kind)) {
    const picked = args.map(a => (a || "").trim()).filter(Boolean);
    if (!picked.length) return "";
    const fn = kind.toUpperCase();
    return `${fn}(${picked.join(", ")})`;
  }
  if (kind === "lookup") {
    const relation = (slots.a || "").trim();
    const field = (slots.b || "").trim();
    if (!relation || !field) return "";
    return `LOOKUP(${relation}, ${field})`;
  }
  const segs = formulaSegments(kind);
  const missing = segs.some(s => "slot" in s && !(slots[s.slot] || "").trim());
  if (missing) return "";
  return segs.map(s => ("lit" in s ? s.lit : slots[s.slot])).join("");
}

/**
 * Parse an expression into builder state, or `null` when it is not something the
 * guided builder can represent.
 *
 * The distinction matters for generated formulas. The grammar the backend
 * accepts is wider than the builder's five shapes — a bare field reference
 * (`amount`) validates and stores fine — so "I can't show this" and "this is
 * empty" are different answers. Collapsing them made the builder open on an
 * empty AVERAGE for any expression it didn't recognize, which read as a
 * half-built formula with nothing explaining why.
 */
export function tryParseFormula(src: string): FormulaState | null {
  const s = (src || "").trim();
  if (!s) return null;
  let m: RegExpMatchArray | null;
  if ((m = s.match(/^(SUM|AVERAGE|COUNT)\(\s*([\s\S]+?)\s*\)$/i))) {
    const kind = VARIADIC_FN[m[1].toUpperCase()];
    const args = splitTopLevel(m[2]).filter(Boolean);
    return { kind, slots: {}, args };
  }
  if ((m = s.match(/^DATEDIF\(\s*([^,]+?)\s*,\s*([^,]+?)\s*,\s*"d"\s*\)$/i))) {
    return { kind: "datedif", slots: { a: m[1], b: m[2] }, args: [] };
  }
  if ((m = s.match(/^LOOKUP\(\s*([^,]+?)\s*,\s*(.+?)\s*\)$/i))) {
    return { kind: "lookup", slots: { a: m[1].trim(), b: m[2].trim() }, args: [] };
  }
  if (/^IF\(/i.test(s)) {
    const ifState = parseIf(s);
    if (ifState) return { kind: "if", slots: {}, args: [], ifState };
  }
  return null;
}

/** Builder state for `src`, falling back to an empty AVERAGE (the default kind). */
export function parseFormula(src: string): FormulaState {
  return tryParseFormula(src) ?? { kind: "average", slots: {}, args: [] };
}

// --- Legacy/label migration + validation --------------------------------------

/**
 * Rewrite every field token in a parsed formula to its canonical key, using the
 * fields on offer. Tokens that resolve by label (pre-contract preview formulas)
 * are converted; tokens that resolve to nothing are left as-is and reported so
 * the caller can mark them broken.
 */
export function resolveFormulaState(
  state: FormulaState,
  fields: FormulaFieldOption[],
  lookupFields: FormulaFieldOption[] = [],
): { state: FormulaState; unresolved: string[] } {
  const unresolved: string[] = [];
  const convert = (token: string, pool: FormulaFieldOption[]): string => {
    const raw = (token || "").trim();
    if (!raw) return raw;
    const res = resolveFormulaField(raw, pool);
    if (res.status === "ok") return res.option.key;
    unresolved.push(raw);
    return raw;
  };

  if (isVariadicKind(state.kind)) {
    return {
      state: { ...state, args: state.args.map(a => convert(a, fields)) },
      unresolved,
    };
  }
  if (state.kind === "datedif") {
    return {
      state: { ...state, slots: { a: convert(state.slots.a, fields), b: convert(state.slots.b, fields) } },
      unresolved,
    };
  }
  if (state.kind === "lookup") {
    // The relation is a fixed keyword, not a field — only the target is resolved.
    return {
      state: { ...state, slots: { a: (state.slots.a || "").trim(), b: convert(state.slots.b, lookupFields) } },
      unresolved,
    };
  }
  if (state.kind === "if" && state.ifState) {
    return {
      state: {
        ...state,
        ifState: {
          ...state.ifState,
          conds: state.ifState.conds.map(c => ({ ...c, field: convert(c.field, fields) })),
        },
      },
      unresolved,
    };
  }
  return { state, unresolved };
}

// --- Result type --------------------------------------------------------------

/**
 * What the backend will store for this formula. Sorting a formula column needs
 * the right OpenSearch subfield (`value_numeric` vs `value_datetime` vs
 * keyword), and re-parsing the expression at render time to find out would be
 * wasteful — so the builder stamps the type into `fieldConfig` at save time.
 */
export function inferFormulaResultType(
  kind: FormulaKind,
  opts: {
    ifState?: IfFormula;
    /**
     * LOOKUP only: the field the second argument points at, as a whole option
     * rather than just its type — a formula target's real type lives in its
     * `resultType` (see `lookupResultType`).
     */
    lookupTarget?: Pick<FormulaFieldOption, "type" | "resultType">;
  } = {},
): FormulaResultType {
  if (kind === "sum" || kind === "average" || kind === "count" || kind === "datedif") return "number";
  if (kind === "if") {
    const { ifState } = opts;
    if (!ifState) return "string";
    const numeric = (v: string) => v.trim() !== "" && !Number.isNaN(Number(v.trim()));
    return numeric(ifState.then) && numeric(ifState.else) ? "number" : "string";
  }
  if (kind === "lookup") return lookupResultType(opts.lookupTarget);
  return "string";
}

/**
 * What `LOOKUP(relation, target)` evaluates to.
 *
 * A target that is ITSELF a formula field answers with its own `resultType`,
 * never with its literal type. `"formula"` is a member of `NUMERIC_TYPES` — it
 * has to be, so a numeric formula can be picked as a SUM/AVERAGE argument (see
 * `isFormulaFieldEligible`) — but reading that membership here typed every
 * formula-targeting LOOKUP as a number. `LOOKUP(company, field_rep_email)`
 * pointed at an account formula returning an email address was stamped
 * `number`, and `customColumnSortField` then routed the column at
 * `properties.<crmName>.value_numeric`, which is empty for text values: the
 * column sorted on nothing and every row tied. The two-field LOOKUP chain is
 * the recommended way to port a Salesforce cross-object formula, so this shape
 * recurs.
 *
 * A formula target with no stamped `resultType` falls back to `"string"`:
 * `value_lowercase.keyword` is always populated, whereas guessing `number`
 * wrongly produces exactly the dead sort above.
 */
function lookupResultType(
  target?: Pick<FormulaFieldOption, "type" | "resultType">,
): FormulaResultType {
  const t = (target?.type || "").trim().toLowerCase();
  if (t === "formula") {
    const result = (target?.resultType || "").trim().toLowerCase();
    return result === "number" || result === "date" ? result : "string";
  }
  if (DATE_TYPES.includes(t)) return "date";
  if (NUMERIC_TYPES.includes(t)) return "number";
  return "string";
}
